import {
  Box,
  Chip,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

// The shortlist switcher, shared by the two CAM surfaces that have one: the
// shortlists tab and the preview pane in Care search. Both answer the same
// question — which of this circle's lists am I looking at — so they read the
// same, and a change to how a list is named or badged happens once.
//
// Pipe-through MUI: a select, a chip per option, and the create item under a
// divider. No per-instance styling, so the theme owns the look.
//
// What it deliberately does not carry: any action on the list. Publish, Edit and
// the rest sit beside the select at the call site, because they differ per
// surface and an action inside a menu row is reachable only by opening the menu.

export type ShortlistOption = {
  id: string;
  name: string;
  status: 'draft' | 'published';
};

// The create item's value. Not a shortlist id, and prefixed so it can never
// collide with one.
const CREATE_VALUE = '__create';

const ShortlistSelect = ({
  lists,
  value,
  onChange,
  onCreate,
  labels,
}: {
  lists: readonly ShortlistOption[];
  value: string;
  onChange: (id: string) => void;
  /** Picked from the foot of the menu. */
  onCreate: () => void;
  /** Passed in so each surface keeps its own content module as the source. */
  labels: { draft: string; published: string; create: string };
}) => {
  const current = lists.find((l) => l.id === value) ?? lists[0];
  const statusOf = (l: ShortlistOption) =>
    l.status === 'published' ? labels.published : labels.draft;
  const colorOf = (l: ShortlistOption) =>
    l.status === 'published' ? ('limeGreen' as const) : ('warmOrange' as const);

  return (
    <TextField
      slotProps={{
        select: {
          // No field label anywhere this is used: the trigger already reads as the
          // name of the thing you are looking at.
          renderValue: () => (
            <Stack
              sx={{ alignItems: 'center', minWidth: 0 }}
              direction='row'
              spacing={1.5}
            >
              <Typography variant='subtitle1' noWrap>
                {current.name}
              </Typography>
              {/* The chip rides in the closed field too, and it is the only status
                marker: it says draft or published in words, which a coloured dot
                only ever said to someone who already knew the code. */}
              <Chip
                size='small'
                label={statusOf(current)}
                color={colorOf(current)}
              />
            </Stack>
          ),
        },
      }}
      select
      size='small'
      fullWidth
      value={current.id}
      // The create item is a value the select can be set to and never holds:
      // picking it opens the dialog and leaves the selection where it was, so a
      // cancelled create doesn't move a CAM off their list.
      onChange={(e) =>
        e.target.value === CREATE_VALUE ? onCreate() : onChange(e.target.value)
      }
    >
      {lists.map((l) => (
        <MenuItem key={l.id} value={l.id}>
          <Stack
            sx={{ alignItems: 'center', width: '100%' }}
            direction='row'
            spacing={1.5}
          >
            {/* The name leads, at the same weight it has in the trigger, so the
                thing you picked and the thing you are looking at read as one. One
                line per option, so the chips line up down the menu. */}
            <Typography
              sx={{ flex: 1, minWidth: 0 }}
              variant='subtitle1'
              noWrap
            >
              {l.name}
            </Typography>
            <Box sx={{ flexShrink: 0 }}>
              <Chip size='small' label={statusOf(l)} color={colorOf(l)} />
            </Box>
          </Stack>
        </MenuItem>
      ))}

      {/* Under a divider, at the foot. It is not one of the lists, so it sits
          apart from them, and it is last because a CAM opening this menu is
          almost always here to switch rather than to start something. */}
      <Divider />
      <MenuItem value={CREATE_VALUE}>
        <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1.5}>
          <Icon icon={'plus' as never} fontSize={14} />
          <Typography variant='subtitle1'>{labels.create}</Typography>
        </Stack>
      </MenuItem>
    </TextField>
  );
};

ShortlistSelect.displayName = 'ShortlistSelect';
export default ShortlistSelect;
